import { Anchor, Button } from '@mantine/core';
import { Video } from 'react-feather';

/**
 * MED-1112 — shared "Join video call" affordance for the meeting link.
 *
 * Opens the resolved meeting URL in a new tab (rel-safe), with a generic,
 * platform-neutral label (Zoom / Teams / Meet all read "Join video call").
 * Renders nothing when there's no link, so callers can drop it in
 * unconditionally and the "in-person → no link" case just disappears.
 *
 * Two presentations:
 *  - `button` — the filled green CTA used on the participant waiting room and
 *    the participant live top bar (clearer tap target).
 *  - `link` — a colored text link used in the coach / peer-leader live header
 *    metadata, where a full button would be too heavy.
 */
const JOIN_GREEN = '#4A7C59';

export interface JoinVideoCallProps {
  /** Resolved meeting URL. Renders nothing when null/empty. */
  href: string | null | undefined;
  variant?: 'button' | 'link';
  /** Button variant only — stretch to fill the container width (waiting room). */
  fullWidth?: boolean;
  /** Button variant only — Mantine size. Defaults to 'sm'. */
  size?: string;
}

export function JoinVideoCall({
  href,
  variant = 'button',
  fullWidth = false,
  size = 'sm',
}: JoinVideoCallProps) {
  if (!href) return null;

  if (variant === 'link') {
    return (
      <Anchor
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: JOIN_GREEN,
          fontWeight: 600,
          fontSize: 13,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
        }}
      >
        {/* translateY(-2px) keeps the icon aligned with the other (nudged)
            metadata-row icons in the live header, the only place this variant
            is used. */}
        <Video size={14} style={{ transform: 'translateY(-2px)' }} />
        {/* Underline on the text itself — text-decoration on the inline-flex
            container doesn't reliably underline the label across browsers. */}
        <span style={{ textDecoration: 'underline' }}>Join video call</span>
      </Anchor>
    );
  }

  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size={size}
      fullWidth={fullWidth}
      leftSection={<Video size={16} />}
      style={{ backgroundColor: JOIN_GREEN, border: 'none' }}
    >
      Join video call
    </Button>
  );
}

export default JoinVideoCall;
